import React, { useEffect, useState } from "react";
import _ from 'lodash';
import moment from 'moment';
import { DatePicker, Button, Select, Table, Tooltip, Popconfirm, Input, Space, Checkbox, Modal ,Typography } from "antd"; // Import Modal
import { DeleteTwoTone, RightCircleTwoTone } from '@ant-design/icons';
import CardDiv from "modules/common/card-div/CardDiv";
import { useToast } from "modules/navbar/top/store/toasterStore";
import { useApprovalsPageState } from "./store";
import dayjs from 'dayjs';
import { truncateString } from "./constants";


const { RangePicker } = DatePicker;
const { Text } = Typography;


const ApprovalsTracker = () => {
    const { setToaster } = useToast();

    const defaultValueofDate = [
         dayjs().startOf("month"),
         dayjs().endOf('month')
    ];
    
    
    const [filterDateRange, setFilterDateRange] = useState(defaultValueofDate);
    const [error, setError] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [remarks, setRemarks] = useState("");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [bulkComment, setBulkComment] = useState("");


    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const handleTableChange = (pagination) => { 
        setPageSize(pagination?.pageSize);
        setCurrentPage(pagination?.current);
    };

    const { isLoading, approvalsData, myTeamDataArray, selectedFilter, updateSelectedFilter, fetchApprovalFunc, ApprovalLeaveActionFunc } = useApprovalsPageState();
    const handleSelectAllChange = (e) => {
        const checked = e.target.checked;
        const selectedKeys = checked ? approvalsData.leaveRecord.map(item => item.leaveRequestId) : [];
        setSelectedRowKeys(selectedKeys);
    }
    const columns = [
        {
            title: <Checkbox 
                        checked={selectedRowKeys?.length === approvalsData?.leaveRecord.length && approvalsData?.totalCount > 0}
                        disabled={approvalsData?.totalCount === 0}
                        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < approvalsData.leaveRecord.length}
                        onChange={handleSelectAllChange} 
                    />,
            dataIndex: 'checkbox',
            key: 'checkbox',
            render: (text, record) => (
                <Checkbox 
                    checked={selectedRowKeys.includes(record.leaveRequestId)} 
                    onChange={() => handleSelectChange(record.leaveRequestId)} 
                />
            )
        },
        {
            title: 'Name',
            dataIndex: 'userName',
            key: 'userName',
        },
        {
            title: 'Leave Type',
            dataIndex: 'leaveName',
            key: 'leaveName',
            render: (leaveName, record) => <span> {leaveName} ({record.leaveLabel}) </span>,
        },
        {
            title: 'RM',
            dataIndex: 'reportingManagerName',
            key: 'reportingManagerName',
            render: (reportingManagerName, record) => <span> {reportingManagerName || "NA"}</span>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Applied On',
            dataIndex: 'appliedDate',
            key: 'appliedDate',
            render: (date) => <>
                <div className="d-flex">
                    {date === null ? "-" : moment(date).format('MMMM DD, YYYY')}
                </div>
            </>,
        },
        {
            title: 'Leave Dates',
            dataIndex: 'leaveDateRange',
            key: 'leaveDateRange',
        },
        {
            title: 'Leave Reason',
            dataIndex: 'leaveReason',
            key: 'leaveReason',
            render: (reason) => <>
                <Tooltip title={_.isEmpty(reason) ? '-' : reason} arrow={false}>
                    <div className="d-flex">
                        {_.isEmpty(reason) ? '-' : truncateString(reason, 30)}
                    </div>
                </Tooltip>
            </>,
        },
        {
            title: "Approved By",
            dataIndex: 'approverName',
            key: 'approverName',
            render: (name) =>
                <div className="d-flex">
                    {_.isEmpty(name) ? '-' : name}
                </div>
        },
        {
            title: 'Approved On',
            dataIndex: 'approverActionDate',
            key: 'approverActionDate',
            render: (date) => <>
                <div className="d-flex">
                    {date === null ? "-" : moment(date).format('MMMM DD, YYYY')}
                </div>
            </>,
        },
        {
            title: "Approver's Comment",
            dataIndex: 'approverComment',
            key: 'approverComment',
            render: (comment) => <Tooltip title={_.isEmpty(comment) ? '-' : comment} arrow={false}>
                <div className="d-flex">
                    {_.isEmpty(comment) ? '-' : truncateString(comment, 30)}
                </div>
            </Tooltip>,
        },
        {
            title: 'Action',
            dataIndex: 'leaveRequestId',
            key: 'leaveRequestId',
            render: (id, record) => {
                return (<>
                    <div className="d-flex gap-3">
                        <Tooltip title="Approve">
                            <Button disabled={record.status === "REJECTED" || record.status === "APPROVED" || record.isMyProfile} type="primary" icon={<RightCircleTwoTone />}
                                onClick={() => { ApprovalLeaveActionFunc(id, "Approved", true, setToaster) }} />
                        </Tooltip>

                        <Popconfirm title="Reject User Leave" description={
                            <Input placeholder="Comment" variant="filled" value={remarks}
                                onChange={(e) => setRemarks(e.target.value)} />}
                            onCancel={() => setRemarks("")}
                            onConfirm={() => { ApprovalLeaveActionFunc(id, remarks, false, setToaster); setRemarks("") }} >
                            <Button disabled={record.status === "REJECTED" || record.isMyProfile} type="primary" danger icon={<DeleteTwoTone />} />
                        </Popconfirm>
                    </div >
                </>)
            },
        },
    ];
    const resetFilters = () => {
        setFilterDateRange(null);
        setCurrentPage(1);
        setPageSize(10);
        updateSelectedFilter("startDate", null);
        updateSelectedFilter("endDate", null);
        updateSelectedFilter("leaveStatus", "");
        updateSelectedFilter("userId", "");
        fetchApprovalBaseFunc({ startDate: null, endDate: null, leaveStatus: "", pageSize: 10, pageNumber: 1, isFirstTime: true });
    };

    useEffect(() => {
        resetFilters(); // Reset filters on component mount
    }, []);

    const handleRangeChange = (dateRange) => {
        if(!dateRange || dateRange.length < 2){
            setError("Please Select The Date");
            setFilterDateRange(dateRange);
            return;
        }

        const date1 = dateRange[0].format("YYYY-MM-DD");
        const date2 = dateRange[1].format("YYYY-MM-DD");

        const diffDays = Number(moment(date2).diff(moment(date1), "days"));
              if (diffDays > 30) {
                setError("The date range exceeds one month. Please adjust your selection.");
                setFilterDateRange(dateRange);
              }
              else{
                setError(null);
                setFilterDateRange(dateRange);
                updateSelectedFilter("startDate", date1);
                updateSelectedFilter("endDate", date2);
                fetchApprovalBaseFunc({ startDate: date1, endDate: date2 });
              }          
}

    const handleChangeSelectedItem = (value) => {
        updateSelectedFilter("leaveStatus", value);
        fetchApprovalBaseFunc({ leaveStatus: value });
    };

    const handlePageChange = (page, size) => {
        updateSelectedFilter("pageNumber", page);
        updateSelectedFilter("pageSize", size);
    }

    function fetchApprovalBaseFunc(overrides = {}) {
        const currentMonthStart = moment().startOf("month").format("YYYY-MM-DD");
        const currentMonthEnd = moment().endOf("month").format("YYYY-MM-DD");
        const {
            userId = selectedFilter.userId,
            startDate = selectedFilter.startDate || currentMonthStart,
            endDate = selectedFilter.endDate || currentMonthEnd,
            leaveStatus = selectedFilter.leaveStatus,
            isFirstTime = false
        } = overrides;
        fetchApprovalFunc(setToaster, { userId, startDate, endDate, leaveStatus, isFirstTime });
    }

    const handleSelectChange = (id) => {
        const selectedKeys = [...selectedRowKeys];
        const index = selectedKeys.indexOf(id);
        if (index >= 0) {
            selectedKeys.splice(index, 1);
        } else {
            selectedKeys.push(id);
        }
        setSelectedRowKeys(selectedKeys);
    };

    const handleBulkApprove = () => {
        ApprovalLeaveActionFunc(selectedRowKeys, "Approved", true, setToaster);
    };

    const handleBulkReject = () => {
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        ApprovalLeaveActionFunc(selectedRowKeys, bulkComment, false, setToaster);
        setBulkComment("");
        setSelectedRowKeys([]);
        setIsModalVisible(false);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
    };

    return (
        <CardDiv title={<h3 className="fw-bold">Approval Tracker </h3>}
            component={
                <>
                    <div className="d-flex gap-2 align-items-center justify-content-end mb-3">
                        <Space>
                        <div className="d-flex " style={{ flexDirection:"column" ,  marginBottom: error ? "21px" : "0",}}>

                        {error && (
                                    <Text type="danger" style={{ display: 'block' }}>
                                     {error}
                                     </Text>
                             )}
                                <RangePicker   
                                       value={filterDateRange} 
                                       format="YYYY-MM-DD"
                                       onChange={handleRangeChange} 
                                       status={error ? 'error' : ''}  
                                       />
                        </div>             
                            <Select className="my-3"
                                value={selectedFilter.leaveStatus}
                                style={{ width: "150px" }}
                                onChange={(value) => { updateSelectedFilter("leaveStatus", value); fetchApprovalBaseFunc({ leaveStatus: value }); }}>
                                <Select.Option value=""> All Status </Select.Option>
                                <Select.Option value="APPROVED"> Approved</Select.Option>
                                <Select.Option value="PENDING"> Pending</Select.Option>
                                <Select.Option value="REJECTED"> Rejected</Select.Option>
                            </Select>
                            <Select className="my-3"
                                value={selectedFilter.userId}
                                style={{ width: "150px" }}
                                onChange={(value) => { updateSelectedFilter("userId", value); fetchApprovalBaseFunc({ userId: value }); }}>
                                {myTeamDataArray?.map((team) => <Select.Option key={team.value} value={team.value}> {team.label} </Select.Option>)}
                            </Select>
                        </Space>
                    </div>

                    <div className="d-flex justify-content-end mb-3 gap-2">
                        <Button type="primary" onClick={handleBulkApprove} disabled={!selectedRowKeys.length}>
                            Bulk Approve
                        </Button>
                        <Button danger onClick={handleBulkReject} disabled={!selectedRowKeys.length} className="ml-2">
                            Bulk Reject
                        </Button>
                    </div>

                    <Table 
                        columns={columns}
                        dataSource={approvalsData?.leaveRecord}
                        loading={isLoading} 
                        onChange={handleTableChange}
                        pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                position: ["bottomLeft"],
                                total: approvalsData?.totalCount,
                                showSizeChanger: true,
                        }}
                        rowKey="leaveRequestId" 
                    />

                    <Modal title="Bulk Reject Comment"
                        visible={isModalVisible}
                        onOk={handleModalOk}
                        onCancel={handleModalCancel}
                    >
                        <Input.TextArea
                            placeholder="Enter comment for rejection"
                            value={bulkComment}
                            onChange={(e) => setBulkComment(e.target.value)}
                        />
                    </Modal>
                </>
            }
        />
    );
};

export default ApprovalsTracker;
